/**
 * @copyright (C) 2025, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Soyun Lee (sylee2@rsupport.com)
 *
 * @description
 *
 * @created at Wed May 14 2025
 **/
import { useState, useEffect } from "react";

import Language, { _T } from "@rfice/language";
import { LocalData } from "@/frameworks/libraries/storage";

import EmojiData from "../../components/Chat/Emoji/emoji.json";

const RECENT_EMOJIS = "emojiCel";

type EmojiType = {
	emoji: string;
	skin_tone_support: boolean;
	name: {
		en: string;
		ko: string;
		jp: string;
	}[];
	slug: string;
	unicode_version: string;
	emoji_version: string;
};

export default function useEmojiManager(focusEmoji: string) {
	const currentLanguage = Language.current();
	const languageMap: Record<string, "en" | "ko" | "jp"> = {
		ja: "jp",
		en: "en",
		ko: "ko"
	};
	const languageKey = languageMap[currentLanguage] || "en";
	const EmojiList = EmojiData.map((category) => ({
		slug: category.slug,
		emojis: category.emojis,
		name: category.name.find((n) => n[languageKey])?.[languageKey] || category.name[0].en
	}));
	const getRecentEmoji = LocalData.get(RECENT_EMOJIS);
	const [recentUseEmoji, setRecentUseEmoji] = useState<string[]>([]);

	useEffect(() => {
		if (getRecentEmoji) {
			setRecentUseEmoji(getRecentEmoji);
		}
	}, []);

	const saveRecentEmoji = (slug: string) => {
		const updated = [slug, ...recentUseEmoji.filter((e) => e !== slug)].slice(0, 50);
		setRecentUseEmoji(updated);
		LocalData.set(RECENT_EMOJIS, updated);
	};

	const getActiveEmojis = () => {
		if (focusEmoji === "recent") {
			const recentEmoji = recentUseEmoji
				.map((emojiSlug) =>
					EmojiData.find((category) => category.emojis.some((e) => e.slug === emojiSlug))?.emojis.find(
						(e) => e.slug === emojiSlug
					)
				)
				.filter(Boolean) as EmojiType[];

			return [
				{
					slug: "recent",
					emojis: recentEmoji,
					name: _T("최근 사용한 이모지")
				},
				...EmojiList
			];
		}

		const activeCategory = EmojiList.find((emoji) => emoji.slug === focusEmoji);
		return activeCategory ? [activeCategory] : [];
	};

	return {
		activeEmojis: getActiveEmojis(),
		saveRecentEmoji
	};
}
